import axios from "axios";
import type { ResolvedTrackEventInput } from "./types";

const EVENTS_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/events/event-data/insert`;

type DeviceDefaults = { device_id: string; os: string; make: string; model: string };

let cachedDefaults: DeviceDefaults | null = null;

// Fallback fingerprinting for when native plugins are missing
const getBrowserFingerprint = async (): Promise<string> => {
  try {
    const signals = [
      window.navigator.userAgent,
      window.navigator.language,
      window.navigator.platform,
      window.navigator.hardwareConcurrency || "unknown",
      (window.navigator as any).deviceMemory || "unknown",
      window.screen.width + "x" + window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
    ].join('###');
    const msgUint8 = new TextEncoder().encode(signals);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    return "app-" + Math.random().toString(36).substring(2, 15);
  }
};

async function getDeviceDefaults(): Promise<DeviceDefaults> {
  if (cachedDefaults) return cachedDefaults;

  const fingerprint = await getBrowserFingerprint();
  const fallback: DeviceDefaults = { device_id: fingerprint, os: inferOsFromUserAgent(), make: "", model: "" };

  try {
    // @ts-ignore - Module might not be installed in all environments
    const { Device } = await import("@capacitor/device");
    const [info, id] = (await Promise.all([
      Device.getInfo().catch(() => ({})),
      Device.getId().catch(() => ({ identifier: "" })),
    ])) as any[];

    cachedDefaults = {
      device_id: id.identifier || fingerprint,
      os: info.operatingSystem || fallback.os,
      make: info.manufacturer || "",
      model: info.model || "",
    };
  } catch (e) {
    cachedDefaults = fallback;
  }
  return cachedDefaults;
}

const getCustomerId = (): string => {
  try {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return "";

    const decodedToken = JSON.parse(atob(accessToken.split(".")[1]));
    return decodedToken && decodedToken["customer-id"]
      ? decodedToken["customer-id"]
      : "";
  } catch (error) {
    console.warn("Failed to decode access token:", error);
    return "";
  }
};

function stringifyPayload(payload: unknown): string {
  if (payload == null) return "{}";
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed.length ? trimmed : "{}";
  }
  try {
    const s = JSON.stringify(payload);
    return s && s.length ? s : "{}";
  } catch {
    return "{}";
  }
}

function inferOsFromUserAgent(): string {
  try {
    const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
    if (/android/.test(ua)) return "android";
    if (/iphone|ipod|ipad/.test(ua)) return "ios";
  } catch { }
  return "app";
}

function getUAETime(): string {
  // Get current time in UAE timezone (Asia/Dubai, UTC+4) in ISO 8601 format
  const date = new Date();
  // Convert to UAE time by getting the time string in Dubai timezone
  const uaeTimeString = date.toLocaleString('en-US', {
    timeZone: 'Asia/Dubai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Parse the UAE time string and create a new date
  const [datePart, timePart] = uaeTimeString.split(', ');
  const [month, day, year] = datePart.split('/');
  const [hour, minute, second] = timePart.split(':');

  // Create date in UAE timezone and return ISO string
  const uaeDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
  const milliseconds = date.getMilliseconds().toString().padStart(3, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}Z`;
}

export async function trackEvent(input: ResolvedTrackEventInput): Promise<void> {
  const defaults = await getDeviceDefaults();

  const data = {
    event_timestamp: input.event_timestamp ?? getUAETime(),
    app_id: "",
    event_name: input.event_name,
    event_category: input.event_category,
    module: input.module ?? "",
    device_id: input.device_id ?? defaults.device_id,
    os: input.os ?? inferOsFromUserAgent(),
    make: input.make ?? defaults.make,
    model: input.model ?? defaults.model,
    latitude: input.latitude ?? "",
    longitude: input.longitude ?? "",
    customer_id: getCustomerId(),
    merchant_id: input.merchant_id ?? "",
    kiosk_id: input.kiosk_id ?? "",
    payload: stringifyPayload(input.payload),
    install_referrer: input.install_referrer ?? "",
    campaign_id: input.campaign_id ?? "",
  };

  try {
    await axios.post(EVENTS_ENDPOINT, data, {
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    console.error("trackEvent(app) failed", err);
  }
}